import ajaxRequest from '../../../helpers/ajaxRequest';
import removeChildNodes from '../../../helpers/removeChildNodes';
import isTouchSupported from '../../../helpers/isTouchSupported';
import animateMiniPopup from './animateMiniPopup';
import { API, MiniFavouritesData, MiniEntry, ImageTransformation } from '../../../types';

interface InitMiniFavourites {
  fetchInitialMiniFavourites: () => Promise<MiniFavouritesData>;
  initMiniFavouritesTouchHoverEvents: (isXLargeUp: boolean) => void;
  cleanUpMiniFavouritesOnViewportResize: () => void;
  updateMiniFavouritesGlobalState: (miniFavouritesData: MiniFavouritesData) => void;
  updateMiniFavouritesMarkup: (miniFavouritesData: MiniFavouritesData) => void;
  updateMiniFavouritesPopupMarkup: ({
    entry,
    isError,
    errorMessage
  }: {
    entry?: MiniEntry;
    isError?: boolean;
    errorMessage?: string;
  }) => void;
}

export const emptyFavouritesData = { entries: [], miniFavouritesDisplayLimit: 0 };
let initialFavouritesPromise: Promise<MiniFavouritesData> = Promise.resolve(emptyFavouritesData);

function initMiniFavourites(): InitMiniFavourites {
  const HIDE = 'hide';
  const HIDE_VISIBILITY = 'hide-visibility';

  const miniFavouritesRootElement = document.querySelector<HTMLLIElement>('.page-header__icons-item--favourites');
  const miniFavouritesLinkElement = miniFavouritesRootElement?.querySelector<HTMLAnchorElement>(
    '.page-header__icons-item-link'
  );
  const miniFavouritesElement = miniFavouritesRootElement?.querySelector<HTMLDivElement>('.mini-favourites');

  const miniFavouritesBadgeElements = document.querySelectorAll<HTMLSpanElement>('[data-favourites-item-counter]');
  const miniFavouritesProductsElement = document.querySelector<HTMLDivElement>('.mini-favourites__products');
  const miniFavouritesProductsListElement = document.querySelector<HTMLUListElement>('.mini-favourites__products-list');
  const miniFavouritesEmptyListElement = document.querySelector<HTMLDivElement>('.mini-favourites__empty');
  const miniFavouritesRemainingElement = document.querySelector<HTMLParagraphElement>(
    '.mini-favourites__products-remaining'
  );
  const miniFavouritesRemainingCounterElement = miniFavouritesRemainingElement?.querySelector<HTMLSpanElement>('span');

  const miniFavouritesPopupElement = document.querySelector<HTMLDivElement>('.mini-favourites-popup');
  const miniFavouritesPopupProductElement = document.querySelector<HTMLDivElement>('.mini-favourites-popup__product');
  const miniFavouritesPopupErrorElement = document.querySelector<HTMLDivElement>('.mini-favourites-popup__error');
  const miniFavouritesPopupErrorMessageElement = document.querySelector<HTMLParagraphElement>(
    '.mini-favourites-popup__error-message'
  );
  const miniFavouritesPopupErrorMessageGenericElement = document.querySelector<HTMLParagraphElement>(
    '.mini-favourites-popup__error-message--generic'
  );
  const miniFavouritesCallToActionElement = document.querySelector<HTMLDivElement>(
    '.mini-favourites-popup__call-to-action'
  );

  function updateMiniFavouritesGlobalState(miniFavouritesData: MiniFavouritesData): void {
    window.bbGlobal.miniFavouritesData.entries = miniFavouritesData.entries;
    window.bbGlobal.miniFavouritesData.miniFavouritesDisplayLimit = miniFavouritesData.miniFavouritesDisplayLimit;
  }

  function updateMiniFavouritesBadgeMarkup(totalEntries: number): void {
    [...miniFavouritesBadgeElements].forEach((badgeElement) => {
      badgeElement.textContent = `${totalEntries}`;
      badgeElement.classList[totalEntries > 0 ? 'remove' : 'add'](HIDE);
      if (totalEntries >= 100) {
        badgeElement.classList.add(HIDE);
      }
    });
  }

  function updateMiniFavouritesProductsMarkup(entries: MiniEntry[]): void {
    removeChildNodes(miniFavouritesProductsListElement);
    entries.forEach((entry) => {
      const listItem = document.createElement('li');
      listItem.classList.add('products-list-item');

      if (entry.productUnavailable) {
        listItem.classList.add('products-list-item--unavailable');
      }

      const listItemLink = document.createElement('a');
      listItemLink.classList.add('products-list-item-link');
      listItemLink.href = entry.productUrl;

      const listItemLinkImage = document.createElement('img');
      listItemLinkImage.classList.add('products-list-item-link-image');
      listItemLinkImage.src = entry.productImage;
      listItemLinkImage.srcset = `${entry.productImage}?$${ImageTransformation.LOUNHMF}$ 1x, ${entry.productImage}?$${ImageTransformation.LOUNHMF_R}$ 2x`;
      listItemLinkImage.alt = '';

      const listItemInfo = document.createElement('div');
      listItemInfo.classList.add('products-list-item-info');

      const listItemInfoManufacturerContainer = document.createElement('div');
      listItemInfoManufacturerContainer.classList.add('products-list-item-info-manufacturer-container');

      const [manufacturer, productName] = entry.productTitle.split('&#32;');

      const listItemInfoManufacturer = document.createElement('p');
      listItemInfoManufacturer.classList.add('products-list-item-info-manufacturer');
      listItemInfoManufacturer.innerHTML = manufacturer;

      const listItemInfoTitle = document.createElement('p');
      listItemInfoTitle.classList.add('small-text', 'products-list-item-info-title');
      listItemInfoTitle.innerHTML = productName;

      const listItemInfoUnavailableMessage = document.createElement('p');
      listItemInfoUnavailableMessage.classList.add('small-text', 'products-list-item-info-unavailable-message');
      listItemInfoUnavailableMessage.textContent = window.bbGlobal.miniFavouritesData.unavailableMessage;

      const listItemInfoPrices = document.createElement('ul');
      listItemInfoPrices.classList.add('products-list-item-info-prices');

      if (entry.priceUponEnquiry === true) {
        const listItemInfoPriceUponEnquiry = document.createElement('p');
        listItemInfoPriceUponEnquiry.textContent = window.bbGlobal.miniFavouritesData.priceUponEnquiryMessage;
        listItemInfoPrices.appendChild(listItemInfoPriceUponEnquiry);
      } else {
        if (entry.productWasWasPrice) {
          const listItemInfoWasWasPrice = document.createElement('p');
          listItemInfoWasWasPrice.classList.add('small-text', 'products-list-item-info-prices-was-price');
          listItemInfoWasWasPrice.textContent = entry.productWasWasPrice;
          listItemInfoPrices.appendChild(listItemInfoWasWasPrice);
        }

        if (entry.productWasPrice) {
          const listItemInfoWasPrice = document.createElement('p');
          listItemInfoWasPrice.classList.add('small-text', 'products-list-item-info-prices-was-price');
          listItemInfoWasPrice.textContent = entry.productWasPrice;
          listItemInfoPrices.appendChild(listItemInfoWasPrice);
        }

        const listItemInfoPrice = document.createElement('p');
        listItemInfoPrice.classList.add('small-text', 'products-list-item-info-prices-price');
        if (entry.productDiscounted) {
          listItemInfoPrice.classList.add('products-list-item-info-prices-price--discounted');
        }

        listItemInfoPrice.textContent = entry.productPrice;
        listItemInfoPrices.appendChild(listItemInfoPrice);
      }

      listItem.appendChild(listItemLink);
      listItemLink.appendChild(listItemLinkImage);
      listItem.appendChild(listItemInfo);
      listItemInfo.appendChild(listItemInfoManufacturerContainer);
      listItemInfoManufacturerContainer.appendChild(listItemInfoManufacturer);
      listItemInfo.appendChild(listItemInfoTitle);
      listItemInfo.appendChild(listItemInfoUnavailableMessage);
      listItemInfo.appendChild(listItemInfoPrices);
      miniFavouritesProductsListElement?.appendChild(listItem);
    });
  }

  function updateMiniFavouritesRemainingProductsMarkup(totalEntries: number): void {
    const remainingEntries = totalEntries - window.bbGlobal.miniFavouritesData.maxMiniLimit;
    if (miniFavouritesRemainingElement && miniFavouritesRemainingCounterElement) {
      miniFavouritesRemainingElement.classList[remainingEntries > 0 ? 'remove' : 'add'](HIDE);
      miniFavouritesRemainingCounterElement.textContent = `${remainingEntries}`;
    }
  }

  function updateMiniFavouritesMarkup({ entries, miniFavouritesDisplayLimit }: MiniFavouritesData): void {
    updateMiniFavouritesBadgeMarkup(entries.length);
    updateMiniFavouritesProductsMarkup(entries.reverse().slice(0, miniFavouritesDisplayLimit));
    updateMiniFavouritesRemainingProductsMarkup(entries.length);
    miniFavouritesProductsElement?.classList[entries.length > 0 ? 'remove' : 'add'](HIDE);
    miniFavouritesEmptyListElement?.classList[entries.length > 0 ? 'add' : 'remove'](HIDE);
  }

  function updateMiniFavouritesPopupMarkup({
    entry,
    isError = false,
    errorMessage = ''
  }: {
    entry?: MiniEntry;
    isError?: boolean;
    errorMessage?: string;
  }): void {
    removeChildNodes(miniFavouritesPopupProductElement);
    miniFavouritesPopupErrorElement?.classList[isError ? 'remove' : 'add'](HIDE);
    miniFavouritesPopupProductElement?.classList[isError ? 'add' : 'remove'](HIDE);
    miniFavouritesCallToActionElement?.classList[isError ? 'add' : 'remove'](HIDE);
    if (isError && miniFavouritesPopupErrorMessageElement) {
      miniFavouritesPopupErrorMessageElement.innerHTML = errorMessage;
      miniFavouritesPopupErrorMessageElement.classList[errorMessage ? 'remove' : 'add'](HIDE);
      miniFavouritesPopupErrorMessageGenericElement?.classList[errorMessage ? 'add' : 'remove'](HIDE);
    }
    if (!isError && entry) {
      const productImage = document.createElement('img');
      productImage.classList.add('mini-favourites-popup__product-image');
      productImage.src = entry.productImage;
      productImage.srcset = `${entry.productImage}?$${ImageTransformation.LOUNHMF}$ 1x, ${entry.productImage}?$${ImageTransformation.LOUNHMF_R}$ 2x`;
      productImage.alt = '';
      miniFavouritesPopupProductElement?.appendChild(productImage);
    }
    animateMiniPopup(miniFavouritesPopupElement, miniFavouritesRootElement, false);
  }

  function fetchInitialMiniFavourites(): Promise<MiniFavouritesData> {
    const { isFavouritesEnabled, isDataFetching, isInitialDataFetched } = window.bbGlobal.miniFavouritesData;

    if (!isFavouritesEnabled || isDataFetching || isInitialDataFetched) {
      return initialFavouritesPromise;
    }

    window.bbGlobal.miniFavouritesData.isDataFetching = true;
    initialFavouritesPromise = ajaxRequest({ method: 'GET', url: API.FAVOURITES_ROLLOVER })
      .then((miniFavouritesData) => {
        updateMiniFavouritesGlobalState(miniFavouritesData);
        updateMiniFavouritesMarkup(miniFavouritesData);
        window.bbGlobal.miniFavouritesData.isInitialDataFetched = true;
        return miniFavouritesData;
      })
      .catch(() => {
        console.error('Something went wrong with favourites');
        return emptyFavouritesData;
      })
      .finally(() => {
        window.bbGlobal.miniFavouritesData.isDataFetching = false;
      });

    return initialFavouritesPromise;
  }

  function miniFavouritesHoverHandler(): void {
    if (
      miniFavouritesElement?.classList.contains(HIDE_VISIBILITY) &&
      miniFavouritesPopupElement?.classList.contains(HIDE) &&
      window.bbGlobal.miniFavouritesData.isInitialDataFetched &&
      window.bbGlobal.miniFavouritesData.isDataFetching === false
    ) {
      showMiniFavourites();
      document.addEventListener('mousemove', function hideOnOutsideHover(event: MouseEvent) {
        if ((event.target as Element).closest('.page-header__icons-item--favourites') === null) {
          hideMiniFavourites();
          document.removeEventListener('mousemove', hideOnOutsideHover);
        }
      });
    }
  }

  function miniFavouritesTouchHandler(e: TouchEvent): void {
    if (
      e.touches.length === 1 &&
      miniFavouritesElement?.classList.contains(HIDE_VISIBILITY) &&
      miniFavouritesPopupElement?.classList.contains(HIDE) &&
      window.bbGlobal.miniFavouritesData.isInitialDataFetched &&
      window.bbGlobal.miniFavouritesData.isDataFetching === false
    ) {
      e.preventDefault();
      showMiniFavourites();
      document.addEventListener(
        'touchstart',
        function hideOnOutsideTouch(event: TouchEvent) {
          if ((event.target as HTMLElement).closest('.page-header__icons-item--favourites') === null) {
            event.stopPropagation();
            event.preventDefault();
            hideMiniFavourites();
            document.removeEventListener('touchstart', hideOnOutsideTouch);
          }
        },
        { passive: false }
      );
    }
  }

  function showMiniFavourites(): void {
    miniFavouritesElement?.classList.remove(HIDE_VISIBILITY);
  }

  function hideMiniFavourites(): void {
    miniFavouritesElement?.classList.add(HIDE_VISIBILITY);
  }

  function initMiniFavouritesTouchHoverEvents(isXLargeUp: boolean): void {
    if (isXLargeUp) {
      miniFavouritesLinkElement?.addEventListener('mouseover', miniFavouritesHoverHandler);
      if (isTouchSupported()) {
        miniFavouritesLinkElement?.addEventListener('touchstart', miniFavouritesTouchHandler);
      }
    } else {
      miniFavouritesLinkElement?.removeEventListener('mouseover', miniFavouritesHoverHandler);
      if (isTouchSupported()) {
        miniFavouritesLinkElement?.removeEventListener('touchstart', miniFavouritesTouchHandler);
      }
    }
  }

  function cleanUpMiniFavouritesOnViewportResize(): void {
    hideMiniFavourites();
  }

  return {
    fetchInitialMiniFavourites,
    initMiniFavouritesTouchHoverEvents,
    cleanUpMiniFavouritesOnViewportResize,
    updateMiniFavouritesGlobalState,
    updateMiniFavouritesMarkup,
    updateMiniFavouritesPopupMarkup
  };
}

export default initMiniFavourites;
